import AppContext from 'App/AppContext';
import { IPSeparator, IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import GenericModal from 'components/Dialogs/GenericModal';
import StringValueList, { IStringValueList } from 'components/StringValue/StringValueList';
import { imagePropsCopilotIcon } from 'globalStyles';
import StringValue from 'models/stringValue/stringValue';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetStringValues } from 'services/Api/stringValueService';
import { apiRequest } from 'services/Auth/authConfig';
import { sortOnString } from 'utils/sorting';

interface IStringValueMatchingItemsProps {
  isOpen: boolean;
  onSelect: (item: StringValue, isNew: boolean) => void;
  onClose: () => void;
  matchingStringValues: number[];
  existingStringValues: number[];
  newStringValue: StringValue;
}

const getCannotSelect = (existingStringValues: number[], matchingStringValues: number[]) => {
  return existingStringValues ? matchingStringValues.every((id) => existingStringValues.includes(id)) : false;
};

const StringValueMatchingItems = (props: IStringValueMatchingItemsProps) => {
  const { t } = useTranslation(['translation', 'stringValue']);
  const appContext = useContext(AppContext);
  const [stringValues, setStringValues] = useState<StringValue[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedStringValues, setSelectedStringValues] = useState<StringValue[]>([]);
  const listRef = useRef<IStringValueList | null>(null);

  //all matching string values are part of the existing string values
  const [cannotSelect, setCannotSelect] = useState<boolean>(
    getCannotSelect(props.existingStringValues, props.matchingStringValues),
  );

  //
  // Effects
  //
  useEffect(() => {
    const cannotSelect = getCannotSelect(props.existingStringValues, props.matchingStringValues);
    setCannotSelect(cannotSelect);

    if (props.isOpen) {
      loadData(props.newStringValue, props.matchingStringValues, cannotSelect);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.newStringValue, props.matchingStringValues, props.existingStringValues, props.isOpen]);

  const loadData = async (newStringValue: StringValue, matchingStringValues: number[], cannotSelect: boolean) => {
    try {
      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      let stringValues: StringValue[] = await apiGetStringValues(newStringValue.stringTypeId, accessToken);
      stringValues = stringValues.filter((sv) => matchingStringValues.includes(sv.stringValueId));
      stringValues.sort((a, b) => sortOnString(a.value, b.value));
      setStringValues(stringValues);
      if (stringValues.length > 0 && !cannotSelect) {
        setSelectedStringValues([stringValues[0]]);
        listRef.current?.scroll(0, true);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const onClose = () => {
    setSelectedStringValues([]);
    props.onClose();
  };

  const onSelect = () => {
    if (selectedStringValues && selectedStringValues.length > 0) {
      props.onSelect(
        selectedStringValues[0],
        selectedStringValues[0].stringValueId === props.newStringValue.stringValueId,
      );
    } else {
      props.onSelect(props.newStringValue, true);
    }
    onClose();
  };

  //
  // Main render
  //
  return (
    <GenericModal
      title={t('stringValue:Dialogs.NewCheck.Title')}
      subTitle={t('stringValue:Dialogs.NewCheck.SubTitle', { name: props.newStringValue.value })}
      isOpen={props.isOpen}
      onClose={() => onClose()}
      onSave={onSelect}
      saveButtonText={
        selectedStringValues.length === 0
          ? t('translation:General.Button.Continue')
          : t('translation:General.Button.Select')
      }
      width={'40vw'}
      minWidth={600}
      maxWidth={'95vw'}
      minHeight={400}
      height={600}
      maxHeight={'95vh'}
      imageProps={imagePropsCopilotIcon}
    >
      <IPStack verticalFill>
        {cannotSelect && (
          <IPStack.Item>
            <IPText>{t('stringValue:Dialogs.NewCheck.ActionCannotSelect')}</IPText>
          </IPStack.Item>
        )}
        {!cannotSelect && (
          <IPStack.Item>
            <IPText>{t('stringValue:Dialogs.NewCheck.ActionSelect')}</IPText>
          </IPStack.Item>
        )}
        <IPSeparator />
        {isLoading && (
          <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
            <IPSpinner size={IPSpinnerSize.large} />
          </IPStack>
        )}
        {!isLoading && stringValues.length > 0 && (
          <IPStack.Item grow>
            <StringValueList
              ref={listRef}
              stringValues={stringValues}
              existingStringValues={props.existingStringValues}
              isLoading={isLoading}
              onSelectStringValues={(items) => setSelectedStringValues(items)}
              type={props.newStringValue.stringTypeId}
              canCreateNew={false}
              hideSearch={true}
              singleSelect={true}
              disableExistingItemView={cannotSelect}
            />
          </IPStack.Item>
        )}
      </IPStack>
    </GenericModal>
  );
};

export default StringValueMatchingItems;
